import { ConsentManagerProvider, useConsentManager } from "@c15t/react";
import { gtag } from "@c15t/scripts/google-tag";
import { googleTagManager } from "@c15t/scripts/google-tag-manager";
import type { Script } from "c15t";
import { ReactNode, useEffect } from "react";

import { useSearch } from "common/router/hooks";

import { INITIAL_CLIENT_DATA } from "client/common/utils";

import { useConsentGPCMutation, useGlobalPrivacyControl } from "../hooks";
import { ConsentBanner } from "./ConsentBanner";
import { ConsentBannerInsetProvider } from "./ConsentBannerInsetContext";
import { ConsentSyncProvider } from "./ConsentSyncProvider";

const { clinic: clinicData } = INITIAL_CLIENT_DATA;

/**
 * Build the c15t scripts array once at module level — the clinic data is static
 * and never changes after initial page load.
 */
const scripts: Script[] = [
  ...(clinicData.gtm_container_ids ?? []).map((gtmId) => googleTagManager({ id: gtmId })),
  ...(clinicData.ga_measurement_ids ?? []).map((gaId) => gtag({ id: gaId, category: "marketing" })),
];

interface ConsentProviderProps {
  children: ReactNode;
}
/**
 * Internal component that syncs GPC consent to c15t first, then to backend.
 * When GPC is enabled, we set the consent to false for all categories.
 */
const GpcBackendSync = () => {
  const { saveConsents } = useConsentManager();
  const { mutate: syncGpcConsent } = useConsentGPCMutation();

  useEffect(() => {
    saveConsents("necessary");
    syncGpcConsent();
  }, [saveConsents, syncGpcConsent]);

  return null;
};

export const ConsentProvider = ({ children }: ConsentProviderProps) => {
  const { preview } = useSearch<{ Search: { preview?: string } }>();
  const gpcEnabled = useGlobalPrivacyControl();

  if (preview === "true") {
    return <>{children}</>;
  }

  return (
    <ConsentManagerProvider
      options={{
        mode: "offline",
        consentCategories: ["necessary", "marketing", "measurement", "experience", "functionality"],
        scripts,
        ignoreGeoLocation: true,
        overrides: {
          country: "US",
        },
      }}
    >
      <ConsentSyncProvider>
        {/* Must be inside ConsentManagerProvider — uses useConsentManager() */}
        <ConsentBannerInsetProvider>
          {gpcEnabled && <GpcBackendSync />}
          {children}
          {!gpcEnabled && <ConsentBanner />}
        </ConsentBannerInsetProvider>
      </ConsentSyncProvider>
    </ConsentManagerProvider>
  );
};
